/**
 * Release Board (paged) types
 *
 * Mirrors the backend contract of
 *   GET /api/teams/:teamId/release-board?column=...&cursor=...
 *   GET /api/teams/:teamId/release-board/counts
 * Keep in sync with the backend release-board service.
 */

import type { PendingApproval } from '@/types/approval';

export type ReleaseBoardColumnKey =
  | 'in_review'
  | 'changes_requested'
  | 'released'
  | 'scheduled';

export const RELEASE_BOARD_COLUMN_KEYS: readonly ReleaseBoardColumnKey[] = [
  'in_review',
  'changes_requested',
  'released',
  'scheduled',
];

/** The `?column=` deep-link value as a board column; anything unknown is ignored (null). */
export function parseBoardColumnParam(value: string | null | undefined): ReleaseBoardColumnKey | null {
  return RELEASE_BOARD_COLUMN_KEYS.find((key) => key === value) ?? null;
}

/** One Version menu: all | original | variations | `lang:<code>` (items whose own language is the code). */
export type BoardVersionFilter = 'all' | 'original' | 'variations' | `lang:${string}`;
export const BOARD_LANG_VERSION_PREFIX = 'lang:';
export type BoardNeededByFilter = 'overdue' | 'today' | 'week' | 'none';
export type BoardMediaTypeFilter = 'image' | 'video' | 'document' | 'carousel' | 'text';

export const BOARD_VERSION_VALUES: readonly BoardVersionFilter[] = ['all', 'original', 'variations'];

/** Language code of a `lang:<code>` version filter, else null. */
export function versionFilterLanguage(version: BoardVersionFilter): string | null {
  return version.startsWith(BOARD_LANG_VERSION_PREFIX) ? version.slice(BOARD_LANG_VERSION_PREFIX.length) : null;
}
export const BOARD_NEEDED_BY_VALUES: readonly BoardNeededByFilter[] = [
  'overdue',
  'today',
  'week',
  'none',
];
export const BOARD_MEDIA_TYPE_VALUES: readonly BoardMediaTypeFilter[] = [
  'image',
  'video',
  'document',
  'carousel',
  'text',
];

/**
 * Max length of the board search text. MIRRORS `BOARD_SEARCH_MAX_LENGTH` in
 * backend/src/types/release-board.types.ts: change both together, or every over-long query 400s.
 */
export const BOARD_SEARCH_MAX_LENGTH = 100;

/** Filters applied to every column and to the counts. */
export interface BoardFilters {
  q: string;
  creatorId: number | null;
  /** Label names (case-insensitive match on the backend) */
  labels: string[];
  version: BoardVersionFilter;
  neededBy: BoardNeededByFilter | null;
  mediaType: BoardMediaTypeFilter | null;
}

export const EMPTY_BOARD_FILTERS: BoardFilters = {
  q: '',
  creatorId: null,
  labels: [],
  version: 'all',
  neededBy: null,
  mediaType: null,
};

/** The card DTO plus the board-specific fields. */
export interface BoardItem extends PendingApproval {
  hasChangesSinceLastSend: boolean;
  /** Stable key of the exact item: `<postId>:base` or `<postId>:<variationId>` */
  id: string;
  column: ReleaseBoardColumnKey;
  /** Which version this card is; `language` is the explicit language (null = unknown, show no language label) */
  version: {
    isOriginal: boolean;
    /** the version's own name, null for the original */
    label: string | null;
    /** lowercase language code, null when not known */
    language: string | null;
  };
  /** ISO date the item is needed by (null = no date) */
  neededBy: string | null;
  /** Scheduled column: next occurrence (ISO) */
  nextOccurrenceAt: string | null;
  /** Released column: when it was released (ISO) */
  releasedAt: string | null;
  /** What the caller may do with this exact card (server rulebook); optional on older payloads. */
  permissions?: { canWithdraw?: boolean };
}

/** What the board's items carry; decides which filter menus exist (mirrors backend BoardFacets). */
export interface BoardFacets {
  creators: Array<{ id: number; name: string }>;
  labels: string[];
  /** lowercase language codes */
  languages: string[];
  mediaKinds: BoardMediaTypeFilter[];
  hasVariations: boolean;
  hasNeededBy: boolean;
}

export interface ReleaseBoardPage {
  items: BoardItem[];
  nextCursor: string | null;
  total: number;
}

export type ReleaseBoardCounts = Record<ReleaseBoardColumnKey, number>;

export interface ReleaseBoardPageParams extends Partial<BoardFilters> {
  column: ReleaseBoardColumnKey;
  cursor?: string | null;
  limit?: number;
}
